import React, { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './hooks/useAuth.jsx'
import { ProtectedRoute, ToastProvider } from './components'
import ModuleSwitchRoute from './components/ModuleSwitchRoute.jsx'
// Eager and small: what a Capex address shows while the switch is off. It never loads the module.
import CapexSwitchedOff from './modules/capex/CapexSwitchedOff.jsx'
import InvoicingSwitchedOff from './modules/invoicing/InvoicingSwitchedOff.jsx'
// Eager and small: an old /capital/estimates/:id link, sent on to Project Financials whatever
// Capital's switch says. It imports only the router, never the Capital module.
import EstimateMovedToProjectFinancials from './modules/capital/EstimateMovedToProjectFinancials.jsx'
import { Login, ResetPassword, Profile, Admin } from './routes'
import Portal from './routes/Portal.jsx'
import ClientApproval from './modules/projects/pages/ClientApproval.jsx'
import SignedTicketCopy from './modules/projects/pages/SignedTicketCopy.jsx'
import ClientProposal from './modules/bidworkup/pages/ClientProposal.jsx'
import ClientProposalSign from './modules/bidworkup/pages/ClientProposalSign.jsx'
import VerifyDocument from './shared/pages/VerifyDocument.jsx'
import ClientReleaseSign from './modules/projects/pages/ClientReleaseSign.jsx'
import ClientInvoice from './modules/finance/pages/ClientInvoice.jsx'
import ClientDocumentFolder from './modules/projects/pages/ClientDocumentFolder.jsx'
import PublicInspection from './modules/forms/pages/PublicInspection.jsx'
import { useSessionTimeout } from './hooks/useSessionTimeout.js'
import SessionTimeoutWarning from './components/SessionTimeoutWarning.jsx'
import { NotificationCenterProvider } from './contexts/NotificationContext.jsx'
import ModuleErrorBoundary from './components/ModuleErrorBoundary.jsx'
import OfflineBanner from './shared/components/OfflineBanner.jsx'
import TourProvider from './shared/tour/TourProvider.jsx'
import SuperAdminDashboard from './pages/SuperAdminDashboard.jsx'
import { isAdminHost } from './shared/utils/host'

// Lazy-load each module so they are independent code-split chunks.
// A build/runtime error in one module will NOT break other modules.
const TimekeepingModule = React.lazy(() => import('./modules/timekeeping/TimekeepingModule.jsx'))
const HRModule = React.lazy(() => import('./modules/hr/HRModule.jsx'))
const ProjectsModule = React.lazy(() => import('./modules/projects/ProjectsModule.jsx'))
const CRMModule = React.lazy(() => import('./modules/crm/CRMModule.jsx'))
const BidWorkupModule = React.lazy(() => import('./modules/bidworkup/BidWorkupModule.jsx'))
const DocumentsModule = React.lazy(() => import('./modules/documents/DocumentsModule.jsx'))
const FinanceModule = React.lazy(() => import('./modules/finance/FinanceModule.jsx'))
const FleetModule = React.lazy(() => import('./modules/fleet/FleetModule.jsx'))
const OfficeBoardModule = React.lazy(() => import('./modules/officeboard/OfficeBoardModule.jsx'))
const PdfReaderPage = React.lazy(() => import('./modules/pdfreader/pages/PdfReaderPage.jsx'))
const InventoryModule = React.lazy(() => import('./modules/inventory/InventoryModule.jsx'))
const FormsModule = React.lazy(() => import('./modules/forms/FormsModule.jsx'))
const CapitalModule = React.lazy(() => import('./modules/capital/CapitalModule.jsx'))
// The vendored capex module, behind its host adapter (see modules/capex/CapexRoute.jsx).
const CapexRoute = React.lazy(() => import('./modules/capex/CapexRoute.jsx'))
// The vendored invoicing module, behind its host adapter (see modules/invoicing/InvoicingRoute.jsx).
const InvoicingRoute = React.lazy(() => import('./modules/invoicing/InvoicingRoute.jsx'))
// The AI Office Worker's tab (design change H2): a page that asks the projects service's agent host
// for a one-time address and sends the browser to the Office Worker, a separate product.
const OfficeWorkerRoute = React.lazy(() => import('./modules/office_worker/OfficeWorkerRoute.jsx'))

const ModuleLoader = () => (
  <div className="loading-container">
    <div className="spinner"></div>
    <p>Loading module...</p>
  </div>
)

function App() {
  const { showWarning, secondsRemaining, endedBy, handleStayLoggedIn, handleLogout } = useSessionTimeout();
  const isAdminDomain = isAdminHost();

  if (isAdminDomain) {
    return (
      <AuthProvider>
        <NotificationCenterProvider>
        <ToastProvider>
          <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute requiredRole="system_admin">
                    <SuperAdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
        </ToastProvider>
        </NotificationCenterProvider>
      </AuthProvider>
    )
  }

  return (
    <AuthProvider>
      <NotificationCenterProvider>
      <ToastProvider>
        {/* Guided tours. Mounted HERE, as a sibling of <Routes>, because it is the only slot that
            survives a cross-module navigation — every module renders its own AppHeader inside its
            own route element, so a tour mounted in that chrome would unmount the moment it walked
            the user to the next screen. Deliberately absent from the isAdminHost() branch above:
            that host is the platform Super Admin console, not a client's staff. */}
        <TourProvider>
        <div className="App">
        <OfflineBanner />
        {/* The post-update notice is NOT mounted here any more. It opened itself at every sign-in,
            in front of whatever the person had signed in to do; it now lives behind the
            "What changed" button in the header (shared/components/WhatChangedButton.jsx). */}
        {/* Session Timeout Warning Modal */}
        {showWarning && (
          <SessionTimeoutWarning
            secondsRemaining={secondsRemaining}
            reason={endedBy}
            onStayLoggedIn={handleStayLoggedIn}
            onLogout={handleLogout}
          />
        )}
        
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/approve/:ticketNumber/:code" element={<ClientApproval />} />
          {/* PUBLIC, read-only: the copy of a signed ticket we email the customer in
              place of a PDF attachment. A DIFFERENT token from /approve above — this
              one grants view + download and cannot sign anything. */}
          <Route path="/ticket/:slug/:code" element={<SignedTicketCopy />} />
          {/* PUBLIC, read-only: the proposal and invoice we email a client in place of a PDF
              attachment. Each token grants view + download of ONE document and can neither sign,
              accept nor pay anything. */}
          <Route path="/proposal/:slug/:code" element={<ClientProposal />} />
          <Route path="/invoice/:slug/:code" element={<ClientInvoice />} />
          {/* PUBLIC, and NOT read-only: a single-use token that lets a client sign one proposal.
              A DIFFERENT COLUMN from the copy token above and a different page, so no amount of
              prop drift can turn a "here is your copy" link into one that binds a client to a
              price. The longer path wins the match on its own specificity. */}
          <Route path="/proposal/:slug/:code/sign" element={<ClientProposalSign />} />
          {/* PUBLIC, and NOT read-only: the client signs a lump-sum release, which is the billing
              event that releases funds for lump-sum work. The backend route has been live for
              months; this page is what it has always pointed at, and until now every client who
              followed one of those links landed on the 404 below. */}
          <Route path="/client/sign-release/:slug/:token" element={<ClientReleaseSign />} />
          {/* PUBLIC, and unlike every route above it carries NO TOKEN AT ALL. Anyone holding a
              signed document can check it here: the 64-character fingerprint is the credential, and
              there is nothing to list, search or enumerate. The people who most need this — the
              client's accounts department, their lawyer, an auditor — will never have an account,
              and a check only we can run is worth nothing in the dispute it exists for. The
              fingerprint form is what the QR on a signature certificate points at. */}
          <Route path="/verify" element={<VerifyDocument />} />
          <Route path="/verify/:fingerprint" element={<VerifyDocument />} />
          {/* PUBLIC, read-only: the client's standing folder of signed documents.
              Its token lives on the crm_clients row, which is already (tenant,
              client), so one tenant's link can never reach another tenant's
              documents for the same company. */}
          <Route path="/documents/:slug/:code" element={<ClientDocumentFolder />} />
          {/* The QR sticker on a piece of equipment. UNAUTHENTICATED by design: the field hands
              who use it have no accounts. The tenant is IN the path because there is no session
              to infer it from, and the server refuses when it disagrees with the sticker's own
              tenant. Deliberately NOT wrapped in ProtectedRoute. */}
          <Route path="/inspect/:tenantId/:code" element={<PublicInspection />} />
          
          {/* Protected routes */}
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin-only routes (both admin and master_admin can access) */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute requiredRole="admin">
                <Admin />
              </ProtectedRoute>
            } 
          />

          {/* System Admin ONLY route for tenant provisioning */}
          <Route 
            path="/super-admin" 
            element={
              <ProtectedRoute requiredRole="system_admin">
                <SuperAdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Portal/Dashboard - protected route */}
          <Route 
            path="/portal" 
            element={
              <ProtectedRoute>
                <Portal />
              </ProtectedRoute>
            } 
          />
          
          {/* THE TWELVE STANDARD MODULES below each sit inside ModuleSwitchRoute too (owner, 29 Sep 2026: an
              on/off for every module for each company, on the Super Admin page). A standard module is ON
              unless the server says `false` for this company (shared/constants/modules.js
              STANDARD_MODULES), so while the answer is on its way, or if it never comes, the module
              renders as it always has; once the server says it is switched off, its address sends the
              person back to the portal exactly as a held module's does, and its service refuses them. */}

          {/* Timekeeping Module - protected routes */}
          <Route 
            path="/timekeeping/*" 
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="timekeeping" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Timekeeping">
                    <Suspense fallback={<ModuleLoader />}>
                      <TimekeepingModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            } 
          />
          
          {/* HR Module - protected routes */}
          <Route 
            path="/hr/*" 
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="hr" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="HR">
                    <Suspense fallback={<ModuleLoader />}>
                      <HRModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            } 
          />
          
          {/* Projects Module - protected routes */}
          <Route 
            path="/projects/*" 
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="projects" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Projects">
                    <Suspense fallback={<ModuleLoader />}>
                      <ProjectsModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            } 
          />
          
          {/* CRM Module - protected routes */}
          <Route 
            path="/crm/*" 
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="crm" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="CRM">
                    <Suspense fallback={<ModuleLoader />}>
                      <CRMModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            } 
          />
          
          {/* Bid Request - redirect to Bid Workup module */}
          <Route path="/bid-request" element={<Navigate to="/bidworkup/new-request" replace />} />
          <Route path="/bid-request/*" element={<Navigate to="/bidworkup/new-request" replace />} />

          {/* Bid Workup Module - protected routes */}
          <Route
            path="/bidworkup/*"
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="bidworkup" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Bid Workup">
                    <Suspense fallback={<ModuleLoader />}>
                      <BidWorkupModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            }
          />
          
          {/* Document Control Module - protected routes */}
          <Route 
            path="/documents/*" 
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="documents" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Documents">
                    <Suspense fallback={<ModuleLoader />}>
                      <DocumentsModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            } 
          />
          
          {/* Finance Module - protected routes */}
          <Route 
            path="/finance/*" 
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="finance" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Finance">
                    <Suspense fallback={<ModuleLoader />}>
                      <FinanceModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            } 
          />
          {/* Fleet Module - protected routes */}
          <Route 
            path="/fleet/*" 
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="fleet" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Fleet">
                    <Suspense fallback={<ModuleLoader />}>
                      <FleetModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            } 
          />

          {/* Office Board Module - protected routes */}
          <Route 
            path="/office-board/*" 
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="officeboard" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Office Board">
                    <Suspense fallback={<ModuleLoader />}>
                      <OfficeBoardModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            } 
          />

          {/* Inventory Module - protected routes */}
          <Route
            path="/inventory/*"
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="inventory" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Inventory">
                    <Suspense fallback={<ModuleLoader />}>
                      <InventoryModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            }
          />

          {/* Forms Module - protected routes (per-tenant gated by features_enabled.forms_platform) */}
          <Route
            path="/forms/*"
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="forms" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Forms">
                    <Suspense fallback={<ModuleLoader />}>
                      <FormsModule />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            }
          />

          {/* /capital/* - RETIRED FROM THE MENU (owner, 30 Sep 2026: Project Financials is the one place for
              capital work). Every /capital address now forwards to Project Financials: /capital to its
              Projects board, /capital/:id to the project's cost page there (its award baseline), and an
              old /capital/estimates/:id to the estimate's own pages (modules/capital/CapitalModule.jsx).
              OUTSIDE Capital's switch, as the estimate forward already was: nothing opens in Capital any
              more, so its switch has nothing to hold here, and /capex's own gate answers where it lands.
              The `capital` switch itself stays - the capital estimate's server routes still need it.

              HELD (update 2, decision 6a): Capex and Invoicing below sit inside
              ModuleSwitchRoute, which reads the SERVER'S switch. Until it says on, a typed address or
              a bookmark lands on the portal instead of mounting a module whose service refuses every
              call. Admins included. The one exception
              is a capital estimate's own address, /capex/estimates/:id, where Bid Workup's redirect,
              the bell, the emails and "View Bid" all lead: with Capex off it says the estimate opens
              in Project Financials and who can switch that on (CapexSwitchedOff), never a silent
              bounce (review finding 17) - and only when the server has SAID it is off: a lookup that
              failed says it could not check, with a reload (whenUnknown).

              AN OLD /capital/estimates/:id LINK is answered here, OUTSIDE Capital's switch: the
              estimate does not open in Capital, so it goes on to /capex/estimates/:id whatever
              Capital's switch says, and the capex route's own gate answers (the estimate, the page
              saying it is off, or the page saying it could not check). Behind Capital's gate it
              bounced silently to the portal whenever Capital was off. */}
          <Route
            path="/capital/estimates/:id"
            element={
              <ProtectedRoute>
                <EstimateMovedToProjectFinancials />
              </ProtectedRoute>
            }
          />
          <Route
            path="/capital/*"
            element={
              <ProtectedRoute>
                <ModuleErrorBoundary moduleName="Capital">
                  <Suspense fallback={<ModuleLoader />}>
                    <CapitalModule />
                  </Suspense>
                </ModuleErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Capex Module — jobs, WIP, job cost, change orders, billing, cash flow, risk, bids.
              A splat route: the module owns every path beneath /capex and keeps ?period on all of
              them, so a copied link reproduces the page. Same shape as Capital next door, and
              deliberately NOT nested inside it — the two are separate apps on separate ports. */}
          <Route
            path="/capex/*"
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="capex" fallback={<ModuleLoader />} whenOff={<CapexSwitchedOff />}
                                   whenUnknown={<CapexSwitchedOff couldNotCheck />}>
                  <ModuleErrorBoundary moduleName="Capex">
                    <Suspense fallback={<ModuleLoader />}>
                      <CapexRoute />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            }
          />

          {/* Invoicing Module — pay applications, T&M invoices, receivables, cash application,
              lien waivers, billing forecast. The second vendored module; same splat-route shape as
              Capex above (the module owns every path beneath /invoicing and keeps ?period), and the
              same host adapter pattern. */}
          <Route
            path="/invoicing/*"
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="invoicing" fallback={<ModuleLoader />} whenOff={<InvoicingSwitchedOff />}
                                   whenUnknown={<InvoicingSwitchedOff couldNotCheck />}>
                  <ModuleErrorBoundary moduleName="Invoicing">
                    <Suspense fallback={<ModuleLoader />}>
                      <InvoicingRoute />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            }
          />

          {/* AI Office Worker (design change H2) — held on OFFICE_WORKER_ENABLED plus the company key,
              exactly like Invoicing above. It runs inside Tynvara (Level 2): this page IS the Office
              Worker's inbox, signed in by this app's own token. Not a splat: the inbox routes itself
              with the URL hash (#/inbox, #/weekly, ...). */}
          <Route
            path="/office-worker"
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="office_worker" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="Office Worker">
                    <Suspense fallback={<ModuleLoader />}>
                      <OfficeWorkerRoute />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            }
          />

          {/* PDF Reader Module - protected route */}
          <Route 
            path="/pdf-reader" 
            element={
              <ProtectedRoute>
                <ModuleSwitchRoute moduleId="pdfreader" fallback={<ModuleLoader />}>
                  <ModuleErrorBoundary moduleName="PDF Reader">
                    <Suspense fallback={<ModuleLoader />}>
                      <PdfReaderPage />
                    </Suspense>
                  </ModuleErrorBoundary>
                </ModuleSwitchRoute>
              </ProtectedRoute>
            } 
          />
          
          {/* Default redirect to portal instead of profile */}
          <Route path="/" element={<Navigate to="/portal" replace />} />
          
          {/* 404 fallback */}
          <Route 
            path="*" 
            element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                  <p className="text-gray-600 mb-4">Page not found</p>
                  <a 
                    href="/login" 
                    className="bfs-link"
                  >
                    Return to Login
                  </a>
                </div>
              </div>
            } 
          />
        </Routes>
        </div>
        </TourProvider>
      </ToastProvider>
      </NotificationCenterProvider>
    </AuthProvider>
  )
}

export default App
